import { IActivityStream, ITeam, OperationType, RequestState } from '@shs-digi-care-monorepo/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { signal, WritableSignal } from '@angular/core';
import { ITableActivity, RuleEngineServiceName } from '@shs-digi-care-monorepo/frontend-lib';
import { ActivityStreamEntity } from '../../shared/models/activity.model';

export class IroActivityStreamOperations {
    private static teams: WritableSignal<ITeam[]> = signal([]);

    public static mapActivities(
        activities: IActivityStream[],
        teams: ITeam[],
        translate: TranslateService
    ): ITableActivity[] {
        this.teams.set(teams);
        return activities
            .map((activity) => this.mapActivity(activity, translate) as ITableActivity)
            .filter((activity) => activity.action !== null);
    }

    private static mapActivity(activity: IActivityStream, translate: TranslateService): ITableActivity {
        const [action, status] = this.mapActivityAction(activity, translate);
        let name;
        if (activity.author) {
            name = `${activity.author.firstName} ${activity.author.lastName}`;
        } else if ((activity.entity.current?.['name'] ?? activity.authorId) === RuleEngineServiceName) {
            name = 'Auto Routing';
        } else if (activity.eventType === 'Email') {
            name = 'Email';
        } else {
            name = 'System Event';
        }
        return {
            name,
            created: activity.createdAt,
            action,
            status
        };
    }

    private static mapActivityAction(
        activity: IActivityStream,
        translate: TranslateService
    ): [action: string, status?: RequestState] {
        const {
            entity: { current, original }
        } = activity;

        const entityType = activity.eventType ?? 'Request';
        const rawOperation = activity.operationType ?? 'unknown';
        // The backend emits operation types in inconsistent casing (e.g. "Created" for requests,
        // "EMAIL_SENT" for emails), so compare case-insensitively against the enum (UPPER_SNAKE_CASE).
        const operation = rawOperation.toUpperCase();

        if (entityType === ActivityStreamEntity.Comment) {
            return [translate.instant('common.activity-stream.comment-created'), undefined];
        } else if (entityType === ActivityStreamEntity.Email) {
            switch (operation) {
                case OperationType.EmailSent: {
                    return [
                        translate.instant('common.activity-stream.email-sent', {
                            email: current?.['payload']?.['sendOptions']?.[0]?.['to']?.join(', ')
                        }),
                        undefined
                    ];
                }
                case OperationType.CommentEmailSentAssignee: {
                    return [translate.instant('common.activity-stream.comment-email-sent-assignee'), undefined];
                }
                case OperationType.CommentEmailSentNotifyResponsible: {
                    return [
                        translate.instant('common.activity-stream.comment-email-sent-notify-responsible'),
                        undefined
                    ];
                }
                case OperationType.ScheduledEmailSent:
                    return [
                        translate.instant('common.activity-stream.escalated-after-hours', {
                            hours: current?.['additionalData']?.reminderType, // TODO: To be updated to use interface instead of using string indexer
                            state: this.mapStatusNameToTitleCase(current?.['additionalData']?.requestState) // TODO: To be updated to use interface instead of using string indexer
                        }),
                        undefined
                    ];
                default: {
                    return [
                        translate.instant('common.activity-stream.email-unknown-operation', {
                            operation: rawOperation
                        }),
                        undefined
                    ];
                }
            }
        } else if (entityType === ActivityStreamEntity.IroNamRequest) {
            if (operation === OperationType.Created)
                return [translate.instant('common.activity-stream.request-created'), current?.['status']];
            else if (operation === OperationType.Updated) {
                switch (current?.['status']) {
                    case RequestState.SUBMITTED:
                        // under-review -> submitted
                        if (original?.['status'] === RequestState.UNDER_REVIEW) {
                            const teamString = this.getTeamStringFromId(current?.['team'] || original?.['teamId']);
                            return [
                                translate.instant('common.activity-stream.request-reassigned-to-team', {
                                    team: teamString
                                }),
                                current['status']
                            ];
                        } else if (original?.['status'] === RequestState.SAVED) {
                            return [translate.instant('common.activity-stream.request-submitted'), current?.['status']];
                        } else if (original?.['status'] === RequestState.CLOSED) {
                            return [translate.instant('common.activity-stream.request-reopened'), current?.['status']];
                        }
                        break;
                    case RequestState.REFERRED_OUT:
                        if (original?.['status'] !== RequestState.REFERRED_OUT) {
                            if (current?.['referredOutTeam']) {
                                const teamString = this.getTeamStringFromId(current?.['referredOutTeam']);
                                return [
                                    translate.instant('common.activity-stream.request-referred-out-to-team', {
                                        team: teamString
                                    }),
                                    current?.['status']
                                ];
                            } else {
                                const translationKey = current?.['isInternalReferredOut']
                                    ? 'common.activity-stream.request-referred-out-internally'
                                    : 'common.activity-stream.request-referred-out-externally';

                                return [
                                    translate.instant(translationKey, { email: current?.['referredOutEmail'] }),
                                    current?.['status']
                                ];
                            }
                        }
                        break;
                    case RequestState.PENDING_CUSTOMER_ACTION:
                        if (original?.['status'] !== RequestState.PENDING_CUSTOMER_ACTION) {
                            return [
                                translate.instant('common.activity-stream.requestor-feedback-requested'),
                                current?.['status']
                            ];
                        }
                        break;
                    case RequestState.CLOSED:
                        return [translate.instant('common.activity-stream.request-resolved'), current?.['status']];
                    case RequestState.UNDER_REVIEW: {
                        let tuple: [action: string, status?: RequestState];

                        // send to different team && no assignee.
                        if (original?.['status'] === RequestState.PENDING_CUSTOMER_ACTION) {
                            return [
                                translate.instant('common.activity-stream.requestor-feedback-received'),
                                current?.['status']
                            ];
                        } else if (original?.['status'] === RequestState.REFERRED_OUT) {
                            return [
                                translate.instant('common.activity-stream.request-sent-back-to-cril'),
                                current?.['status']
                            ];
                        } else {
                            // under-review -> under-review
                            if (!!current?.['assignee'] && !!original && !original?.['assignee']) {
                                tuple = [
                                    translate.instant('common.activity-stream.request-assigned-to-user', {
                                        firstName: current?.['assignee']?.firstName,
                                        lastName: current?.['assignee']?.lastName
                                    }),
                                    current?.['status']
                                ];
                            } else if (
                                (typeof current['assignee'] === 'string' &&
                                    typeof original?.['assignee'] === 'string' &&
                                    original?.['assignee'] !== current['assignee']) ||
                                (!!current['assignee']?.['gid'] &&
                                    current['assignee']?.['gid'] !== original?.['assignee'])
                            ) {
                                tuple = [
                                    translate.instant('common.activity-stream.request-reassigned-to-user', {
                                        firstName: current?.['assignee']?.firstName,
                                        lastName: current?.['assignee']?.lastName
                                    }),
                                    undefined
                                ];
                            } else {
                                break;
                            }
                        }
                        return tuple;
                    }
                }
                if (current?.['isOnHold'] && !original?.['isOnHold']) {
                    return [translate.instant('common.activity-stream.request-set-on-hold'), undefined];
                } else if (!current?.['isOnHold'] && original?.['isOnHold']) {
                    return [translate.instant('common.activity-stream.request-set-active'), undefined];
                }
                return [translate.instant('common.activity-stream.request-updated'), current?.['status']];
            }
        } else if (entityType === ActivityStreamEntity.Approval) {
            return [translate.instant('common.activity-stream.approval'), undefined];
        } else if (entityType === ActivityStreamEntity.Attachment) {
            const translationKey = `activity-stream.attachment-${rawOperation.toLowerCase()}`;
            return [translate.instant(translationKey, { filename: current?.['filePathAndName'] }), undefined];
        }

        // Fallback for
        return [
            translate.instant('common.activity-stream.entity-operation', {
                entity: entityType,
                operation: rawOperation
            }),
            RequestState.SAVED
        ];
    }

    private static getTeamStringFromId(teamId: string): string {
        const team = this.teams().find((t) => t.id === teamId);
        return team ? team.name : teamId;
    }

    private static mapStatusNameToTitleCase(status: string): string {
        if (!status) {
            return '';
        }
        return status
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}
